import { Expand, Minimize2, User, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';

const homePageGradientStyle = {
  background:
    'linear-gradient(135deg, #B3E5FC 0%, rgb(225, 212, 248) 25%, #81D4FA 50%, #C5CAE9 75%, #B39DDB 100%)',
  boxShadow:
    '0px 5px 20.1px 0px rgba(255, 255, 255, 0.10), 0px 15px 30px 0px rgba(0, 0, 0, 0.10), 0px 4px 25px 0px rgba(255, 255, 255, 0.35) inset',
  backdropFilter: 'blur(30px)',
};

const glassCardStyle = {
  borderRadius: '12px',
  border: '1px solid var(--card-11-stroke, #FFF)',
  background:
    'linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), linear-gradient(170deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.40) 97.94%)',
  boxShadow:
    '0px 5px 20.1px 0px rgba(255, 255, 255, 0.10), 0px 15px 30px 0px rgba(0, 0, 0, 0.10), 0px 4px 25px 0px rgba(255, 255, 255, 0.35) inset',
  backdropFilter: 'blur(30px)',
};

const faceRecognitionHeaderStyle = {
  background: 'linear-gradient(90deg, rgba(179, 229, 252, 0.92) 0%, rgba(129, 212, 250, 0.88) 50%, rgba(179, 229, 252, 0.9) 100%)',
  boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.5)',
};

const CameraAttendanceScreen = () => {
  const cameraPanelRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [faceMatched, setFaceMatched] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedCamera, setSelectedCamera] = useState('cam1');
  const [selectedPreviewCameraId, setSelectedPreviewCameraId] = useState('active-cam-1');
  const navigate = useNavigate();
  const location = useLocation();
  const { openOverlay } = useOverlay();

  const handleToggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement && cameraPanelRef.current) {
        await cameraPanelRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Failed to toggle fullscreen:', error);
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const nextId = location.state?.selectedPreviewCameraId;
    if (typeof nextId === 'string' && nextId.trim()) {
      setSelectedPreviewCameraId(nextId);
    }
  }, [location.state?.selectedPreviewCameraId]);

  const openVisitorModal = () => {
    setSelectedAction('visitor');
    setFaceMatched(false);
    openOverlay('visitorCheckIn', {
      title: 'Visitor Check-in',
      onSubmit: submittedForm => {
        const name = submittedForm?.employeeToMeet ? String(submittedForm.employeeToMeet) : '';
        setSelectedAction(`visitor - ${name.trim().toLowerCase()}`);
      },
    });
  };

  const personStatus = [
    // TODO: Populate from backend/stream events
  ];

  const cameraOptions = [
    // TODO: Populate from backend
  ];

  const todaysLogs = [];

  const activeCameraFeeds = [];
  const activeCamerasCount = activeCameraFeeds.length;

  const selectedPreviewCamera =
    activeCameraFeeds.find(camera => camera.id === selectedPreviewCameraId) ??
    activeCameraFeeds[0];

  return (
    <div
      className="w-full min-h-full flex flex-col gap-4 rounded-xl "
      style={homePageGradientStyle}
    >
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 flex-1 min-h-0">
        <div className="xl:col-span-8 flex flex-col gap-4 min-h-[360px]">
          <section
            ref={cameraPanelRef}
            className="relative rounded-xl overflow-hidden border border-white/40 bg-black/80 min-h-[360px]"
          >
            <button
              type="button"
              onClick={handleToggleFullscreen}
              className="absolute top-3 left-3 z-20 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-green-600 text-white !text-white text-sm font-medium hover:bg-green-700 transition-colors"
            >
              {isFullscreen ? (
                <>
                  <Minimize2 className="w-4 h-4 text-white" />
                  Exit Full Screen
                </>
              ) : (
                <>
                  <Expand className="w-4 h-4 text-white" />
                  Full Screen
                </>
              )}
            </button>

            {selectedPreviewCamera?.image ? (
              <img
                src={selectedPreviewCamera.image}
                alt={
                  selectedPreviewCamera?.name
                    ? `${selectedPreviewCamera.name} live feed`
                    : 'Live camera feed'
                }
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-sm text-white/80">No live feed available.</p>
              </div>
            )}

            {selectedPreviewCamera?.name ? (
              <div className="absolute bottom-3 left-3 z-20 rounded-lg bg-black/60 px-3 py-2 backdrop-blur-sm">
                <p className="text-sm font-semibold text-white">{selectedPreviewCamera.name}</p>
              </div>
            ) : null}

            <button
              type="button"
              onClick={() =>
                navigate('/camera-attendance/all-feeds', {
                  state: {
                    feeds: activeCameraFeeds,
                    selectedPreviewCameraId,
                      autoFullscreen: true,
                  },
                })
              }
              className="absolute top-3 right-3 z-20 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-sky-600 text-white !text-white text-sm font-medium hover:bg-sky-700 transition-colors"
            >
              View All camera feed
            </button>

            <button
              type="button"
              onClick={() =>
                navigate(`/video-attendance/zones?camera_id=${encodeURIComponent(selectedCamera)}`)
              }
              className="absolute top-14 right-3 z-20 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/80 text-neutral-900 text-sm font-semibold hover:bg-white transition-colors"
              title="Open Zone Designer"
            >
              Edit Zones
            </button>

          </section>

          <div className="rounded-xl p-3 md:p-4" style={glassCardStyle}>
            <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-white/30">
              <div className="flex items-center gap-3">
                <p className="text-3xl font-semibold text-neutral-800">Status</p>
                <select
                  value={selectedCamera}
                  onChange={e => setSelectedCamera(e.target.value)}
                  className="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-700"
                >
                  {cameraOptions.length ? (
                    cameraOptions.map(camera => (
                      <option key={camera.id} value={camera.id}>
                        {camera.name.toLowerCase()}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No cameras
                    </option>
                  )}
                </select>
              </div>
              <p className="text-sm text-neutral-700">
                Active Cameras: <span className="font-semibold">{activeCamerasCount}</span>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3">
              <div className="rounded-lg border border-white/60 bg-white/50 p-3 backdrop-blur-sm">
                <h3 className="text-sm font-bold text-neutral-900 mb-2 uppercase">
                  CURRENT PRESENCE
                </h3>
                <div className="text-sm text-neutral-600">
                  No presence data available yet.
                </div>
              </div>

              <div className="rounded-lg border border-white/60 bg-white/50 p-3 backdrop-blur-sm">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold text-neutral-900 uppercase">TODAY'S FEED LOG</h3>
                  <button
                    type="button"
                    className="text-sm font-semibold text-green-700 hover:text-green-800"
                    onClick={() => navigate('/camera-attendance/logs', { state: { logs: todaysLogs } })}
                  >
                    View All Logs
                  </button>
                </div>
                <div className="space-y-1.5">
                  {todaysLogs.length ? (
                    todaysLogs.slice(0, 5).map((log, index) => (
                      <p key={`${log}-${index}`} className="text-sm text-neutral-700">
                        {log}
                      </p>
                    ))
                  ) : (
                    <p className="text-sm text-neutral-600">No logs yet.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <aside
          className="xl:col-span-4 rounded-xl border border-white/50 p-3 flex flex-col gap-2 min-h-0"
          style={glassCardStyle}
        >
          <div
            className="rounded-xl border border-white/60 px-3 py-3 shrink-0"
            style={faceRecognitionHeaderStyle}
          >
            <h2 className="text-sm font-bold tracking-wide text-neutral-900 uppercase text-center">
              Face Recognition Status
            </h2>
          </div>

          <div
            className="rounded-xl border border-white/60 bg-white/55 flex-1 flex flex-col min-h-0 p-3 backdrop-blur-sm"
            style={{ boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.45)' }}
          >
            <p className="text-sm font-semibold text-neutral-800 mb-2">Detected Person</p>

            <div
              className="rounded-xl border border-sky-200/70 p-3 mb-3"
              style={{
                background:
                  'linear-gradient(180deg, rgba(224, 247, 250, 0.95) 0%, rgba(179, 229, 252, 0.55) 100%)',
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <User
                  className={`w-5 h-5 shrink-0 ${
                    faceMatched ? 'text-emerald-600' : 'text-rose-600'
                  }`}
                  aria-hidden
                />
                <span
                  className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold ${
                    faceMatched
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-rose-100 text-rose-800'
                  }`}
                >
                  {faceMatched ? 'Match Found' : 'Match Not Found'}
                </span>
              </div>

              {!faceMatched ? (
                <p className="text-xs text-neutral-600 mb-3">
                  Entered person face is not matching with the team. Please select visitor if
                  the person is here to visit or else retry again.
                </p>
              ) : (
                <p className="text-xs text-neutral-600 mb-3">
                  Face matched with a team member. Entry is allowed.
                </p>
              )}

              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedAction('daily_wage_employee');
                    setFaceMatched(true);
                  }}
                  className="w-full rounded-lg border border-green-500 bg-white text-green-700 px-3 py-2 text-sm font-medium hover:bg-green-50"
                >
                  daily wage employee
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedAction('retry_face_scan');
                      setFaceMatched(false);
                    }}
                    className="rounded-lg bg-green-600 text-white !text-white px-3 py-2 text-sm font-medium hover:bg-green-700"
                  >
                    Retry Face Scan
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      openVisitorModal();
                    }}
                    className="rounded-lg border border-green-500 bg-white text-green-700 px-3 py-2 text-sm font-medium hover:bg-green-50"
                  >
                    Visitor
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-2 mb-3 flex-1 min-h-0">
              {personStatus.length ? (
                personStatus.map(item => (
                  <div
                    key={item.label}
                    className="flex items-center justify-between gap-3 rounded-lg bg-cyan-50/60 border border-cyan-100 px-2 py-1.5"
                  >
                    <span className="text-sm text-neutral-700">{item.label}</span>
                    <span
                      className={`text-xs px-2 py-1 rounded-md font-semibold ${
                        item.status === 'ok'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-orange-100 text-orange-700'
                      }`}
                    >
                      {item.value}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-neutral-600">No status data yet.</p>
              )}
            </div>

            <div className="rounded-lg bg-white/50 border border-white/50 p-2 mt-auto backdrop-blur-sm">
              <p className="text-xs font-semibold text-neutral-800 mb-1">Message:</p>
              <p className="text-xs text-neutral-700 italic">
                Please wear safety gloves before entering the production zone.
              </p>
              {selectedAction ? (
                <p className="text-[11px] text-neutral-500 mt-2">
                  Last action: {selectedAction.replaceAll('_', ' ')}
                </p>
              ) : null}
            </div>
          </div>
        </aside>
      </div>

    </div>
  );
};

export default CameraAttendanceScreen;
